import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { triggerAlert } from "../../utils";
import { AdminPhoneVerifications } from "../../utils/APIClient";
import { confirm } from "../../common/ConfirmDialog";

/**
 * Compliance-review dialog for one OrgPhoneVerification row.
 *
 * Shows the three uploaded documents (image thumbnails inline, PDFs
 * open in a new tab) alongside the org's status + timestamps. Approve
 * unlocks the customer's Buy Numbers page; Reject requires a written
 * reason that surfaces on the customer's page and enables re-upload.
 */

function DocPreview({ label, url }) {
    if (!url) {
        return (
            <div className="p-3 border rounded text-muted small">
                <strong>{label}</strong> — no file uploaded.
            </div>
        );
    }
    const isImage = /\.(png|jpg|jpeg)(\?|$)/i.test(url);
    return (
        <div className="p-3 border rounded">
            <div className="d-flex justify-content-between align-items-center mb-2">
                <strong>{label}</strong>
                <a
                    href={url} target="_blank" rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-secondary"
                >
                    Open in new tab
                </a>
            </div>
            {isImage ? (
                <img
                    src={url} alt={label}
                    style={{ maxWidth: "100%", maxHeight: 320, borderRadius: 4 }}
                />
            ) : (
                <div className="text-muted small">
                    PDF — click "Open in new tab" to inspect.
                </div>
            )}
        </div>
    );
}

export default function PhoneVerificationDetailDialog({ row, onClose, onDecision }) {
    const [busy, setBusy] = useState(false);
    const [rejectMode, setRejectMode] = useState(false);
    const [reason, setReason] = useState("");
    const reasonRef = useRef(null);

    // Focus the textarea when the reject form opens. React-Bootstrap
    // Modal sets initial focus on the dialog container, so autoFocus
    // alone gets stolen back on the same tick — a short setTimeout
    // moves the caret into the textarea reliably. Runs ONLY on the
    // false → true transition so typing into the field doesn't
    // repeatedly re-steal focus mid-keystroke.
    useEffect(() => {
        if (rejectMode && reasonRef.current) {
            const el = reasonRef.current;
            const t = setTimeout(() => el.focus(), 50);
            return () => clearTimeout(t);
        }
    }, [rejectMode]);

    const approve = async () => {
        const ok = await confirm({
            title: "Approve verification?",
            message: `Approve ${row.org_name}? This unlocks Buy Numbers immediately and emails the customer.`,
            confirmLabel: "Approve",
            variant: "success",
        });
        if (!ok) return;
        setBusy(true);
        try {
            await AdminPhoneVerifications.approve(row.org_id);
            triggerAlert("success", "", `${row.org_name} approved. Customer notified.`);
            onDecision();
        } catch (e) {
            triggerAlert("error", "Oops...",
                e?.response?.data?.error || "Failed to approve.");
        } finally {
            setBusy(false);
        }
    };

    const reject = async () => {
        const r = reason.trim();
        if (!r) {
            triggerAlert("error", "Reason required",
                "Enter a rejection reason so the customer knows what to fix.");
            return;
        }
        setBusy(true);
        try {
            await AdminPhoneVerifications.reject(row.org_id, r);
            triggerAlert("success", "", `${row.org_name} rejected. Reason emailed to customer.`);
            onDecision();
        } catch (e) {
            triggerAlert("error", "Oops...",
                e?.response?.data?.error || "Failed to reject.");
        } finally {
            setBusy(false);
        }
    };

    const canDecide = row.status === "pending" || row.status === "rejected"
                       || row.status === "approved";
    // Only pending is realistically actionable; approved/rejected show
    // as read-only history with the decision buttons still available in
    // case a reviewer wants to flip a decision.

    return (
        <Modal show onHide={onClose} size="lg" centered scrollable>
            <Modal.Header closeButton>
                <Modal.Title>
                    {row.org_name || "Organization"}
                    <span className="ms-2 badge bg-secondary">{row.status}</span>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row g-3 mb-3">
                    <div className="col-md-6">
                        <div className="text-muted small">Organization ID</div>
                        <div className="fw-semibold">{row.org_id}</div>
                    </div>
                    <div className="col-md-6">
                        <div className="text-muted small">Submitted</div>
                        <div>{row.submitted_at
                            ? new Date(row.submitted_at).toLocaleString() : "—"}</div>
                    </div>
                    {row.reviewed_at && (
                        <>
                            <div className="col-md-6">
                                <div className="text-muted small">Reviewed</div>
                                <div>{new Date(row.reviewed_at).toLocaleString()}</div>
                            </div>
                            <div className="col-md-6">
                                <div className="text-muted small">Reviewer</div>
                                <div>{row.reviewed_by || "—"}</div>
                            </div>
                        </>
                    )}
                    {row.rejection_reason && (
                        <div className="col-12">
                            <div className="alert alert-warning py-2 mb-0">
                                <strong>Previous rejection reason:</strong> {row.rejection_reason}
                            </div>
                        </div>
                    )}
                </div>

                <hr />

                <h6 className="mb-3">Uploaded documents</h6>
                <div className="d-flex flex-column gap-3">
                    <DocPreview label="EIN" url={row.ein_url} />
                    <DocPreview label="Company Incorporation Certificate"
                                url={row.incorporation_url} />
                    <DocPreview label="Card Holder US Address Proof"
                                url={row.address_proof_url} />
                </div>

                {rejectMode && (
                    <>
                        <hr />
                        <label className="form-label fw-semibold" htmlFor="phone-verification-reject-reason">
                            Rejection reason (shown to the customer)
                        </label>
                        <textarea
                            id="phone-verification-reject-reason"
                            ref={reasonRef}
                            className="form-control"
                            rows={3}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="e.g. EIN letter is a screenshot rather than the original PDF."
                        />
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button className="btn btn-outline-secondary" onClick={onClose}
                        disabled={busy}>
                    Close
                </button>
                {canDecide && !rejectMode && (
                    <button className="btn btn-outline-danger"
                            onClick={() => setRejectMode(true)} disabled={busy}>
                        Reject…
                    </button>
                )}
                {rejectMode && (
                    <>
                        <button className="btn btn-outline-secondary"
                                onClick={() => { setRejectMode(false); setReason(""); }}
                                disabled={busy}>
                            Cancel rejection
                        </button>
                        <button className="btn btn-danger" onClick={reject}
                                disabled={busy || !reason.trim()}>
                            {busy ? "Rejecting…" : "Confirm reject"}
                        </button>
                    </>
                )}
                {canDecide && !rejectMode && (
                    <button className="btn btn-success" onClick={approve} disabled={busy}>
                        {busy ? "Approving…" : "Approve"}
                    </button>
                )}
            </Modal.Footer>
        </Modal>
    );
}
